import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, catchError, of, tap, throwError } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faUserLock, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

import { GlobalFiltersService, AuthService, LookupService, LeaguesService } from '@app/shared/services';
// import { LeagueDetailsService } from '@app/leagues/services';

import { SpinnerComponent } from '../../../components/loaders/spinner/spinner.component';
import { TablePlaceholderComponent } from '../../../components/loaders/table-placeholder/table-placeholder.component';
import { CardPlaceholderComponent } from '../../../components/loaders/card-placeholder/card-placeholder.component';
import { ErrorComponent } from '../../../components/error/error.component';
import { EmptyComponent } from '../../../components/empty/empty.component';
import { DocumentActionsComponent } from '../../document-actions/document-actions.component';
import { DetailsIconComponent } from '../../action-icons/details-icon.component';
import { StatusIconComponent, StatusTypes } from '../../status-icon/status-icon.component';
import { TeamsBadgeComponent } from '../../../components/entity-badges/teams-badge.component';
import { PlayersBadgeComponent } from '../../../components/entity-badges/players-badge.component';
import { CoachesBadgeComponent } from '../../../components/entity-badges/coaches-badge.component';
import { DocumentsBadgeComponent } from '../../../components/entity-badges/documents-badge.component';
import { LocationBadgeComponent } from '../../../components/entity-badges/location-badge.component';
import { YearsBadgeComponent } from '../../../components/entity-badges/years-badge.component';
import { AppStatusComponent } from '../../../components/app-status/app-status.component';
import { DocumentThumbnailComponent } from '../../../components/document-thumbnail/document-thumbnail.component';

import { ForumComponent } from '../../../components/data/forum/forum.component';

import { PagedModel } from '@app/shared/models';
import { LeagueListViewModel, LeagueSearchItemViewModel } from '../../../models/league.models';

import { LookupView } from '../../../models';

import {
	LookupCodes,
	Messages,
	AppPermissionCodes,
	LeagueFilterCategoryCodes,
	LeagueAgreementStatusCodes,
	AffiliationApplicationStatusCodesV2,
	AppRoleCodes
} from '@app/constants';

interface LeagueFilters {
	// categoryID: number | null;
	q: string | null;
	regionID: number | null;
	zoneID: number | null;
	affiliationStatuses: {
		notStarted: boolean;
		draft: boolean;
		submitted: boolean;
		rejected: boolean;
		approved: boolean;
	},
	leagueAgreementSubmitted: boolean | null;
	tournamentApplicationSubmitted: boolean | null;
	incompleteTeams: boolean | null;
	missingDocuments: boolean | null;
}

// TODO: MOVE THIS INTO THE LEAGUES MODULE

@Component({
	selector: 'app-leagues',
	templateUrl: './leagues.component.html',
	styleUrl: './leagues.component.scss',
	standalone: true,
	imports: [
		CommonModule, RouterModule, FormsModule, FontAwesomeModule, NgbTooltipModule, InfiniteScrollModule,
		SpinnerComponent, TablePlaceholderComponent, CardPlaceholderComponent, ErrorComponent, EmptyComponent, DocumentActionsComponent, DetailsIconComponent, StatusIconComponent, TeamsBadgeComponent, PlayersBadgeComponent, CoachesBadgeComponent, DocumentsBadgeComponent, LocationBadgeComponent, YearsBadgeComponent,
		AppStatusComponent, DocumentThumbnailComponent, ForumComponent
	]
})
export class LeaguesComponent implements OnInit, OnDestroy {
	// internal state
	activeLeagues: LeagueSearchItemViewModel[] = [];
	inactiveLeagues: LeagueSearchItemViewModel[] = [];
	leaguesLoading = true;
	leaguesError = false;

	private leagueYearSubscription: Subscription | null = null;

	filters: LeagueFilters = {
		q: null,
		regionID: null,
		zoneID: null,
		affiliationStatuses: {
			notStarted: true,
			draft: true,
			submitted: true,
			rejected: true,
			approved: true
		},
		leagueAgreementSubmitted: null,
		tournamentApplicationSubmitted: null,
		incompleteTeams: null,
		missingDocuments: null
	};
	private filtersChange = new Subject<string>();
	private filtersChangeSubscription: Subscription | null = null;

	// services
	private toastrService: ToastrService = inject(ToastrService);
	protected globalFiltersService: GlobalFiltersService = inject(GlobalFiltersService);
	protected authService: AuthService = inject(AuthService);
	protected lookupService: LookupService = inject(LookupService);
	private leaguesService: LeaguesService = inject(LeaguesService);

	// lookups
	regionsLookup$!: Observable<LookupView[]>;
	regionsLookupLoading = true;
	zonesLookup$!: Observable<LookupView[]>;
	zonesLookupLoading = true;

	// icons
	protected addIcon = faPlus;
	protected guestUserIcon = faUserLock;
	protected infoIcon = faInfoCircle;

	// constants
	AppPermissionCodes = AppPermissionCodes;
	LeagueFilterCategoryCodes = LeagueFilterCategoryCodes;
	StatusTypes = StatusTypes;
	LeagueAgreementStatusCodes = LeagueAgreementStatusCodes;
	AffiliationApplicationStatusCodes = AffiliationApplicationStatusCodesV2;

	constructor() {
		this.initializeLookups();
	}

	ngOnInit() {
		this.connectLeagueSearch();
		this.leagueYearSubscription = this.globalFiltersService.getLeagueYear$()
			.subscribe(() => {
				if (!this.globalFiltersService.getLeagueYearID()) {
					return;
				}
				this.onFiltersChange();
			});
	}

	ngOnDestroy() {
		this.leagueYearSubscription?.unsubscribe();
		this.filtersChangeSubscription?.unsubscribe();
	}

	private initializeLookups() {
		this.regionsLookup$ = this.lookupService.getLookup<LookupView>(LookupCodes.Regions)
			.pipe(
				tap(() => setTimeout(() => this.regionsLookupLoading = false, 0)),
				catchError(err => {
					setTimeout(() => this.regionsLookupLoading = false, 0);
					return this.showLookupErrorToast('Regions', err);
				})
			);

		this.zonesLookup$ = this.lookupService.getLookup<LookupView>(LookupCodes.Zones)
			.pipe(
				tap(() => setTimeout(() => this.zonesLookupLoading = false, 0)),
				catchError(err => {
					setTimeout(() => this.zonesLookupLoading = false, 0);
					return this.showLookupErrorToast('Zones', err);
				})
			);
	}

	private showLookupErrorToast(lookupName: string, error: any) {
		this.toastrService.error(Messages.ErrorRetry, `Failed to Load '${lookupName}'`);
		return throwError(() => error);
	}

	private connectLeagueSearch() {
		this.filtersChangeSubscription = this.filtersChange
			.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				switchMap(() => {
					this.resetLeagueResults();

					this.leaguesLoading = true;
					this.leaguesError = false;

					return this.leaguesService
						.searchLeagues$(
							this.filters.q,
							this.filters.regionID,
							this.filters.zoneID,
							this.filters.affiliationStatuses.notStarted,
							this.filters.affiliationStatuses.draft,
							this.filters.affiliationStatuses.submitted,
							this.filters.affiliationStatuses.rejected,
							this.filters.affiliationStatuses.approved,
							this.filters.leagueAgreementSubmitted,
							this.filters.tournamentApplicationSubmitted,
							this.filters.incompleteTeams,
							this.filters.missingDocuments
						)
						.pipe(
							// handle fetch errors
							catchError(error => {
								console.error('error searching leagues', error);
								this.leaguesError = true;
								this.leaguesLoading = false;
								return of(null);
							}));
				})
			)
			.subscribe(response => {
				this.processLeaguesData(response);
				this.leaguesLoading = false;
			});
	}

	// onSearchChange(query: string) {
	// 	this.filters.q = query;
	// 	this.onFiltersChange();
	// }

	onFiltersChange() {
		this.emitFiltersChange();
	}

	private emitFiltersChange() {
		this.filtersChange.next(this.buildFiltersSignature());
	}

	private buildFiltersSignature(): string {
		return JSON.stringify({
			leagueYearID: this.globalFiltersService.getLeagueYearID(),
			...this.filters,
		}
			// {
			// leagueYearID: this.globalFiltersService.getLeagueYearID(),
			// q: this.filters.q,
			// regionID: this.filters.regionID,
			// zoneID: this.filters.zoneID,
			// affiliationStatuses: { ...this.filters.affiliationStatuses },
			// leagueAgreementSubmitted: this.filters.leagueAgreementSubmitted,
			// tournamentApplicationSubmitted: this.filters.tournamentApplicationSubmitted,
			// incompleteTeams: this.filters.incompleteTeams,
			// missingDocuments: this.filters.missingDocuments
			// }
		);
	}

	private resetLeagueResults() {
		this.activeLeagues = [];
		this.inactiveLeagues = [];
	}

	async onLoadLeagues() {
		if (this.leaguesLoading) {
			return;
		}

		try {
			this.leaguesLoading = true;
			this.leaguesError = false;

			const allLeagues = await this.leaguesService.searchLeagues(
				this.filters.q,
				this.filters.regionID,
				this.filters.zoneID,
				this.filters.affiliationStatuses.notStarted,
				this.filters.affiliationStatuses.draft,
				this.filters.affiliationStatuses.submitted,
				this.filters.affiliationStatuses.rejected,
				this.filters.affiliationStatuses.approved,
				this.filters.leagueAgreementSubmitted,
				this.filters.tournamentApplicationSubmitted,
				this.filters.incompleteTeams,
				this.filters.missingDocuments
			);
			// const response = await this.leaguesService
			// 	getLeagues(
			// 	1, // TODO: this.filters.categoryID,
			// 	this.filters.q,
			// 	this.filters.zoneID,
			// 	this.filters.regionID,
			// 	this.leaguesCurrentPage
			// );

			this.processLeaguesData(allLeagues);
		} catch (error) {
			this.leaguesError = true;
		} finally {
			this.leaguesLoading = false;
		}
	}

	private processLeaguesData(allLeagues: LeagueSearchItemViewModel[] | null) {
		if (!allLeagues) {
			return;
		}

		if (!this.activeLeagues) {
			this.activeLeagues = [];
		}

		if (!this.inactiveLeagues) {
			this.inactiveLeagues = [];
		}

		if (this.authService.isInRole(AppRoleCodes.SystemAdministrator)) {
			// split leagues into active/inactive for program managers
			this.activeLeagues = allLeagues.filter(league => league.isActive);
			this.inactiveLeagues = allLeagues.filter(league => !league.isActive);
		} else {
			// keep all as active for everyone else
			this.activeLeagues = allLeagues;
			this.inactiveLeagues = [];
		}
	}

	// onUpdateLeagueDetails(league: LeagueListViewModel) {
	// 	this.leagueDetailsService.updateCurrentLeague({
	// 		leagueID: league.leagueId,
	// 		leagueName: league.leagueName
	// 	});
	// }
}
